import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Alert, Button, Select, Spinner, TextInput, phone as phoneLib, useResponsive } from '@parsina/framework';
import { isIranMarket } from '@parsina/framework/ui/lib/brand';
import PhoneInput from '../../components/common/PhoneInput';
import { C } from '@parsina/framework/tokens';

import { useTranslation, toAsciiDigits } from '../../i18n';
import { STACK } from '../../constants/density';
import { StepHeading, PrimaryAction } from '../../components/auth/OnboardingParts.jsx';
import { useShortcut, Kbd } from '../../lib/shortcuts.jsx';
import LocationPicker from '../../components/common/LocationPicker';
import PlaceField from '../../components/common/PlaceField';
import { cityOptions, provinceOptions } from '../../constants/iranGeo';
import { DEFAULT_CLINIC_TYPE } from '../Clinic/Clinic.shared';
import { useClinicTypes } from '../../hooks/useClinicTypes';
import { RECOVERY } from './errors.js';
import { currencyOptions, DEFAULT_CURRENCY } from '../../lib/currencyOptions';
import { countryOptions, DEFAULT_COUNTRY } from '../../lib/countryOptions';
import { isOfferedAbroad, mapViewForCountry } from './accountRegion.js';

export { DEFAULT_COUNTRY };

/**
 * Step 1 of registration — «مشخصات کلینیک».
 *
 * ── Why this is a real `<form>` ─────────────────────────────────────────────
 *
 * It was a `<div>` of raw `<input>`s with an `onClick` on the button, which is
 * the shape that makes Enter do nothing. The clinic app's second doctrine
 * (clinic-app/CLAUDE.md §4.2) is that every high-frequency flow has a keyboard
 * path, and while registration is emphatically not high-frequency, it is the
 * first form the owner of a keyboard-driven product ever meets. A form that
 * ignores Enter teaches the wrong thing on the first screen.
 *
 * ── The field set is not arbitrary ──────────────────────────────────────────
 *
 * Only the name is required, and that is deliberate: everything here is
 * editable afterwards in «کلینیک من» (said out loud, under the button), so
 * demanding an address before a person can start is friction with nothing on
 * the other side of it — `clinicType` included, now that `UpdateClinicInput`
 * carries it and the owner can change it in «کلینیک من».
 *
 * `clinicType` is still the answer that matters most, because it is the one the
 * product branches on: it decides which slice of the service catalogue the
 * clinic's «افزودن خدمت» picker leads with. The vocabulary comes from the
 * `clinicTypes` registry the server also validates against, so this picker
 * cannot offer a key the save would refuse — which is exactly what the four
 * uppercase values the wizard once wrote did, and why a clinic registered
 * through it could not declare itself dental at all.
 *
 * ── The phone ───────────────────────────────────────────────────────────────
 *
 * «شماره تلفن کلینیک» is the clinic's OFFICE line and is therefore a LANDLINE —
 * «۰۲۱۸۸…», «۰۶۵…», «۰۳۲…» — so the kit's `PhoneInput` is given
 * `allowLandline`, which drops Iran's mobile pattern and keeps the ten-digit
 * national-number bound that an area code + subscriber number satisfies
 * exactly. The field folds Persian and Arabic-Indic digits itself and hands
 * back E.164 («+982188000000»), which is the one form a dialler and an SMS
 * gateway can both use; a value like «۰۲۱…» on the wire is neither.
 *
 * ── A founder from abroad ───────────────────────────────────────────────────
 *
 * `region.foreign` (their number is not Iranian — accountRegion.js) removes
 * every Iranian default from this step: the office line starts on THEIR
 * country, province and city are typed rather than picked from Iran's list,
 * IRR is not offered, the currency helper does not name Iran, and the map
 * opens on their country instead of Tehran.
 */

/** The office line's country list for a clinic abroad: the kit's, without Iran. */
const COUNTRIES_ABROAD = phoneLib.COUNTRIES.filter((c) => c.iso2 !== 'IR');

/** Screen order, so «the first invalid field» means the topmost one. */
const FIELD_ORDER = ['name', 'officePhone'];
const FIELD_TEST_IDS = { name: 'clinic-name', officePhone: 'clinic-phone' };

export default function ClinicDetailsStep({
  form,
  onChange,
  onPatch,
  errors,
  serverError,
  onSubmit,
  onRecover,
  submitting,
  disabled,
  region = { foreign: false, country: null },
}) {
  const abroad = region.foreign;
  const iranMarket = isIranMarket();
  const { t, locale, formatNumber } = useTranslation();
  const { isMobile } = useResponsive();
  // The clinic-type vocabulary, from the registry the server validates against —
  // or, until/unless it answers, the hook's local copy of it. Never empty.
  const { options: clinicTypeOptions } = useClinicTypes();
  // Shared with «کلینیک من» (lib/countryOptions.js).
  const countryPickerOptions = React.useMemo(
    () => countryOptions(locale, form.country),
    [locale, form.country],
  );
  const formRef = React.useRef(null);
  const alertRef = React.useRef(null);

  /**
   * Reach a control by its test id.
   *
   * The kit's inputs are not `forwardRef` — they spread the rest of their props
   * onto the real `<input>`, which is what puts `data-testid` there — so this
   * is how a caller focuses one. It is the pattern «همکاران» already uses for
   * its `F3` search shortcut, kept rather than invented.
   */
  const focusField = React.useCallback((testId) => {
    formRef.current?.querySelector(`[data-testid="${testId}"]`)?.focus();
  }, []);

  // A server refusal must not be something you scroll to find.
  React.useEffect(() => {
    if (serverError?.message) alertRef.current?.focus();
  }, [serverError]);

  // The first thing that is wrong, focused — in the field ORDER, not in
  // `Object.keys` order, so "the first one" means the first one on screen.
  const firstInvalid = FIELD_ORDER.find((f) => errors?.[f]);
  React.useEffect(() => {
    if (firstInvalid) focusField(FIELD_TEST_IDS[firstInvalid]);
  }, [firstInvalid, errors, focusField]);

  // Two different reasons a control might be unavailable, and they are NOT the
  // same reason.
  //
  //   `submitting` — a create is in flight. Everything freezes, because the
  //   saga behind it is not re-entrant and a second submit is how a founder
  //   ends up owning two half-built clinics.
  //
  //   `disabled` (offline) — the SUBMIT cannot succeed, but the form is still
  //   worth filling in. Greying out the fields because the network dropped
  //   would throw away what the person has typed the moment a lift or a dead
  //   spot interrupts them; the banner above already explains why the button
  //   will not go.
  const busy = submitting;
  const cannotSubmit = submitting || disabled;

  useShortcut('ctrl+s', () => { if (!cannotSubmit) formRef.current?.requestSubmit(); }, {
    label: t('auth.createClinic.submit'),
    group: t('shortcuts.groups.onboarding'),
    enabled: !cannotSubmit,
  });

  const errorCount = Object.values(errors || {}).filter(Boolean).length;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (cannotSubmit) return;
    onSubmit();
  };

  const recoveryActions = () => {
    if (!serverError) return null;
    if (serverError.recovery === RECOVERY.GO_TO_CLINIC) {
      return (
        <Button variant="outline" onClick={() => onRecover(serverError)}>
          {t('auth.createClinic.goToExisting')}
        </Button>
      );
    }
    if (serverError.recovery === RECOVERY.REPAIR) {
      return (
        <Button variant="outline" onClick={() => onRecover(serverError)}>
          {t('clinic.gate.repairAction')}
        </Button>
      );
    }
    return null;
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} noValidate>
      <StepHeading title={t('auth.steps.details')} subtitle={t('auth.createClinic.subtitle')} />
      <div
        style={{
          display: 'grid',
          // `minmax(0, 1fr)` rather than `1fr`: a track's automatic minimum is
          // its content, so a long Persian province name in a plain `1fr`
          // column widens the grid past the card and takes the page into
          // horizontal scroll. Direction-agnostic — column one is the
          // inline-start column in Persian and in English alike.
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
          gap: 14,
        }}
      >
        <div style={{ gridColumn: '1 / -1' }}>
          <TextInput
            label={t('auth.createClinic.nameLabel')}
            required
            value={form.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder={t('auth.createClinic.namePlaceholder')}
            error={errors.name}
            maxLength={120}
            disabled={busy}
            data-testid="clinic-name"
            aria-invalid={errors.name ? 'true' : undefined}
            aria-label={t('auth.createClinic.nameLabel')}
            autoFocus
          />
        </div>

        {/* The vocabulary comes from the server (`clinicTypes`), which is also
            what `createClinic` validates against — so this picker cannot offer a
            key the save would refuse. While the query is in flight, or when it
            fails, the hook offers this build's copy of the same registry — the
            picker is never locked for want of a reference query.

            The helper no longer says «بعداً قابل تغییر نیست»: `UpdateClinicInput`
            gained the field, so the owner can change it in «کلینیک من». */}
        <Select
          label={t('auth.createClinic.typeLabel')}
          value={form.clinicType || DEFAULT_CLINIC_TYPE}
          options={clinicTypeOptions}
          placeholder={t('auth.createClinic.typePlaceholder')}
          onChange={(e) => onChange('clinicType', e.target.value)}
          helperText={t('auth.createClinic.typeHelperEditable')}
          disabled={busy}
          data-testid="clinic-type"
          aria-label={t('auth.createClinic.typeLabel')}
        />

        {/* «واحد پول» — chosen here, IRR preselected (docs/clinic-currency/
            CONTRACTS.md §7). It is changeable later in «کلینیک من» only until
            the first amount is recorded, which is why the helper says so. The
            kit `Select` takes `options`; the testid sits on a wrapper because the
            kit does not spread it onto the control. */}
        <div data-testid="clinic-currency">
          <Select
            label={t('clinic.fields.currency')}
            value={form.currency || DEFAULT_CURRENCY}
            options={abroad
              ? currencyOptions(t, locale).filter((o) => isOfferedAbroad(o.value) || o.value === form.currency)
              : currencyOptions(t, locale)}
            placeholder={null}
            onChange={(e) => onChange('currency', e.target.value)}
            helperText={t(abroad ? 'clinic.currency.createHelperAbroad' : 'clinic.currency.createHelper')}
            disabled={busy}
            aria-label={t('clinic.fields.currency')}
          />
        </div>

        {/* «کشور محل فعالیت» — the clinic's JURISDICTION (PM-0082, root
            CLAUDE.md «Clinic jurisdiction»). Preselected by the screen from the
            number the founder signed in with, IR when that number says nothing;
            the founder confirms or changes it. Full width, because a country
            name in a half column wraps in Arabic. */}
        <div style={{ gridColumn: '1 / -1' }} data-testid="clinic-country">
          <Select
            label={t('clinic.fields.country')}
            // Outside the Iranian market an unknown jurisdiction stays EMPTY and
            // must be chosen (validateClinicDraft): shown as IR, it would be sent
            // as nothing, and nothing is Iran on the wire.
            value={form.country || (iranMarket ? DEFAULT_COUNTRY : '')}
            options={countryPickerOptions}
            placeholder={iranMarket ? null : t('auth.onboarding.foreign.countryPlaceholder')}
            onChange={(e) => onChange('country', e.target.value)}
            error={errors.country}
            helperText={errors.country ? undefined : t('clinic.country.createHelper')}
            disabled={busy}
            aria-label={t('clinic.fields.country')}
          />
        </div>

        {/* Both halves are stored: `officePhone` is the canonical E.164 the save
            sends, `officePhoneDraft` is what is on screen. E.164 is `''` for an
            incomplete number, so without the draft a half-typed «۰۲۱۱۲» would be
            indistinguishable from an untouched field and the wizard would drop
            it in silence rather than refuse it — see `validateClinicDraft`. */}
        <PhoneInput
          // The kit reads `defaultCountry` once, at mount, and "abroad" is only
          // known after a round trip — so the field starts over when it arrives.
          key={abroad ? `abroad-${region.country || ''}` : 'home'}
          label={t('auth.createClinic.phoneLabel')}
          allowLandline
          // «۲۱۸۸…» beside «+۹۸», not «۰۲۱۸۸…» — the same spelling «مشخصات کلینیک»
          // edits it in (see ClinicProfileForm). A typed zero still parses.
          showTrunk={false}
          // The kit's own example is Iran's MOBILE one; this field takes an office
          // line, so it says so. Explicit, because a caller-supplied placeholder is
          // the only way to override it. From abroad the line is in THEIR country,
          // and the kit's example for that country beats a Tehran number.
          defaultCountry={abroad && region.country ? region.country : undefined}
          countries={abroad ? COUNTRIES_ABROAD : undefined}
          placeholder={abroad ? undefined : t('auth.createClinic.phonePlaceholder')}
          value={form.officePhone}
          onChange={(e164, meta) => onPatch({ officePhone: e164, officePhoneDraft: meta.draft })}
          helperText={errors.officePhone ? undefined : t('auth.createClinic.phoneHelper')}
          error={errors.officePhone}
          disabled={busy}
          data-testid="clinic-phone"
          aria-label={t('auth.createClinic.phoneLabel')}
        />

        {/* The SAME picker «کلینیک من» edits with, so a clinic registered here
            and one corrected later agree on how تهران is spelt. Iran's list means
            nothing to a founder from abroad, who types their own. */}
        {abroad ? (
          <>
            <TextInput
              label={t('auth.createClinic.provinceLabel')}
              value={form.province}
              onChange={(e) => onChange('province', e.target.value)}
              maxLength={120}
              dir="auto"
              disabled={busy}
              data-testid="clinic-province"
              aria-label={t('auth.createClinic.provinceLabel')}
            />
            <TextInput
              label={t('auth.createClinic.cityLabel')}
              value={form.city}
              onChange={(e) => onChange('city', e.target.value)}
              maxLength={120}
              dir="auto"
              disabled={busy}
              data-testid="clinic-city"
              aria-label={t('auth.createClinic.cityLabel')}
            />
          </>
        ) : (
          <>
            <PlaceField
              label={t('auth.createClinic.provinceLabel')}
              value={form.province}
              options={provinceOptions(form.province, locale)}
              placeholder={t('auth.createClinic.provincePlaceholder')}
              onChange={(value) => onPatch({ province: value, city: '' })}
              disabled={busy}
              testId="clinic-province"
            />
            <PlaceField
              label={t('auth.createClinic.cityLabel')}
              value={form.city}
              options={cityOptions(form.province, form.city, locale)}
              placeholder={t('auth.createClinic.cityPlaceholder')}
              onChange={(value) => onChange('city', value)}
              disabled={busy || !form.province}
              helperText={form.province ? undefined : t('auth.createClinic.cityHelper')}
              testId="clinic-city"
            />
          </>
        )}

        <div style={{ gridColumn: '1 / -1' }}>
          <TextInput
            label={t('auth.createClinic.addressLabel')}
            value={form.address}
            onChange={(e) => onChange('address', e.target.value)}
            placeholder={t('auth.createClinic.addressPlaceholder')}
            maxLength={300}
            disabled={busy}
            data-testid="clinic-address"
            aria-label={t('auth.createClinic.addressLabel')}
          />
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <LocationPicker
            latitude={form.latitude}
            longitude={form.longitude}
            abroad={abroad}
            openAt={abroad ? mapViewForCountry(region.country) : undefined}
            onPick={({ latitude, longitude, province, city, address }) => {
              onPatch({
                latitude,
                longitude,
                ...(province && { province }),
                ...(city && { city }),
                ...(address && { address }),
              });
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular, marginTop: 20 }}>
        {errorCount ? (
          <Alert
            variant="warning"
            role="alert"
            icon={<AlertTriangle size={16} aria-hidden="true" />}
            data-testid="clinic-form-invalid"
          >
            {t('validation.form.fixCount', { count: formatNumber(errorCount) })}
          </Alert>
        ) : null}

        {serverError?.message ? (
          <div ref={alertRef} tabIndex={-1} style={{ outline: 'none' }}>
            <Alert
              variant="error"
              role="alert"
              data-testid="clinic-form-error"
              action={recoveryActions()}
            >
              {serverError.message}
            </Alert>
          </div>
        ) : null}

        <PrimaryAction
          type="submit"
          fullWidth
          disabled={cannotSubmit}
          aria-busy={submitting || undefined}
          aria-keyshortcuts="Control+S"
          data-testid="clinic-submit"
        >
          {submitting ? <Spinner size={16} color="#fff" label={t('common.state.loading')} /> : null}
          {submitting ? t('auth.createClinic.submitting') : t('auth.createClinic.submit')}
          {submitting ? null : <Kbd combo="ctrl+s" />}
        </PrimaryAction>

        <p style={{ margin: 0, fontSize: 12, color: C.textMuted, lineHeight: 1.8, textAlign: 'center' }}>
          {t('auth.createClinic.editLater')}
        </p>
      </div>
    </form>
  );
}

/**
 * Validate a draft. Returns `{ errors, input }` — the errors to show and, when
 * there are none, exactly what goes on the wire.
 *
 * Exported because the screen owns submission and the test owns the rules; a
 * validator that can only be reached by rendering a form is a validator that
 * gets tested through six clicks.
 */
export function validateClinicDraft(form, t) {
  const errors = {};
  const name = String(form.name || '').trim();
  if (!name) errors.name = t('validation.clinic.nameRequired');
  else if (name.length < 2) errors.name = t('validation.clinic.nameTooShort');
  // Outside the Iranian market the jurisdiction is a choice, never a default:
  // left empty it would travel as nothing, and nothing is Iran (see `country`).
  if (!isIranMarket() && !form.country) errors.country = t('auth.onboarding.foreign.countryPlaceholder');

  // A value that reached the draft some other way — restored from storage, or a
  // caller building the input directly — is judged by the KIT, not by a local
  // regex. `allowLandline`, because «شماره تلفن کلینیک» is the office line and
  // Iran's mobile pattern would refuse every area code. The local `/^0\d{9,10}$/`
  // it replaces accepted «0218800000» — a Tehran number one digit short — and
  // waved every `+…` string through unread; `isValidPhone` reads both.
  //
  // The fold and the separator strip stay: they shape what goes ON THE WIRE for
  // this legacy path, which is the national spelling the draft carried. The
  // judgement above them is the kit's.
  const phone = toAsciiDigits(String(form.officePhone || '')).replace(/[\s-]/g, '');
  const typed = toAsciiDigits(String(form.officePhoneDraft ?? '')).replace(/[\s-]/g, '');
  if (phone && !phoneLib.isValidPhone(phone, { allowLandline: true })) {
    errors.officePhone = t('validation.phone.invalidLandline');
  } else if (!phone && typed) {
    // Something was typed and it is not a number. The field is OPTIONAL, so the
    // dangerous answer is not "invalid" — it is silently sending nothing while
    // the owner believes they gave the clinic's line.
    errors.officePhone = t('validation.phone.invalidLandline');
  }

  // The currency travels only when it is not IRR: absent IS IRR on every BFF, and
  // an older one that has never heard of the field keeps accepting the request.
  const currency = form.currency && form.currency !== DEFAULT_CURRENCY ? form.currency : undefined;
  // The country likewise: absent IS IR on the BFF and the service, and an older
  // BFF that has never heard of the field keeps accepting an Iranian clinic.
  const country = form.country && form.country !== DEFAULT_COUNTRY ? form.country : undefined;

  const input = {
    name,
    clinicType: form.clinicType || DEFAULT_CLINIC_TYPE,
    ...(currency ? { currency } : {}),
    ...(country ? { country } : {}),
    officePhone: phone || undefined,
    province: String(form.province || '').trim() || undefined,
    city: String(form.city || '').trim() || undefined,
    address: String(form.address || '').trim() || undefined,
    latitude: form.latitude ?? undefined,
    longitude: form.longitude ?? undefined,
  };

  return { errors, input };
}
